<template>
	<PopUp
		:title="isUpgrade ? 'Abonnement upgraden' : 'Abonnement'"
		:ok-disabled="!dataValid || purchasing"
		size="xl"
		ok-text="Kostenpflichtig bestellen"
		:hide-actions="purchasing || !dataComplete"
		:hide-close="purchasing"
		@ok="onOk"
		@hidden="$emit('hidden')"
	>
		<Loading v-if="loading" text="Lade Daten..."/>
		<template v-else>
			<CompleteData
				v-if="!dataComplete && customer"
				:customer="customer"
				@complete="dataComplete = true"
			/>
			<template v-else>
				<Loading v-if="purchasing"
						 text="Die Bestellung wird initialisiert, bitte schließe das Browserfenster nicht!"
						 class="mt-4"/>
				<template v-else>
					<div class="mb-5">
						<h4>So funktioniert's:</h4>
						<HowItWorks class="my-4" :on-white="true"/>
					</div>
					<PriceTable
						:with-free="false"
						custom-button-click-handler
						:chosen-product="product"
						:upgradable-items="upgradableItems"
						@buy="onChoseProduct"
					/>
					<template v-if="product">
						<div class="mt-4">
							<div class="font-weight-bold font-size-big4 mb-2">
								<span :class="'color-' + productColor">{{ product.label }}:</span>
								<span>
									{{ priceToPay.price | moneyAmount }} (inkl. {{ priceToPay.taxRate }}% USt.)
								</span>
							</div>
							<template v-if="hasMollieCustomerId">
								Nach Klick auf <b>Kostenpflichtig bestellen</b> buchen wir den Betrag automatisch von
								der
								von dir gewählten Zahlungsmethode ab. Sollte das nicht funktionieren, wirst du auf die
								Seite
								unseres Zahlungsdienstleisters (MOLLIE) weitergeleitet, wo du aus verschiedenen
								Zahlungsarten wählen kannst.
							</template>
							<template v-else>
								Nach Klick auf <b>Kostenpflichtig bestellen</b> wirst du auf die Seite unseres
								Zahlungsdienstleisters (MOLLIE) weitergeleitet, wo du aus verschiedenen Zahlungsarten
								wählen
								kannst.
							</template>
						</div>
						<div class="payment-logos mt-3">
							<ul>
								<li v-for="method in paymentMethods" :key="method.key">
									<img :src="'/img/payment/method/' + method.key + '.svg'" :alt="method.name"/>
								</li>
							</ul>
						</div>
						<div class="mt-4">
							<PrivacyCheckbox v-model="privacy"/>
						</div>
					</template>
					<InfoBox v-if="error" type="error" class="mt-4">
						Bei der Bezahlung ist ein Fehler aufgetreten. Bitte versuche es erneut oder wende dich an
						unseren <a href="#" @click.prevent="openFeedback">Support</a>.
					</InfoBox>
				</template>
			</template>
		</template>
	</PopUp>
</template>
<script>
import PopUp from "@/vue/common/PopUp";
import FormGroup from "@/vue/common/form/Group";
import RadioGroup from "@/vue/common/form/RadioGroup";
import GetList from "@/api/product/GetList";
import {SUBSCRIPTION} from "@/helper/product/Category";
import _ from "lodash";
import {isDefined} from "@/helper/Value";
import Loading from "@/vue/common/Loading";
import Purchase from "@/api/payment/Purchase";
import InfoBox from "@/vue/common/InfoBox";
import Get from "@/api/auth/Get";
import CompleteData from "@/vue/customer/CompleteData.vue";
import Info from "@/api/customer/subscription/upgrade/Info";
import PriceTable from "@/vue/payment/PriceTable.vue";
import {PRODUCT_COLORS} from "@/helper/product/Product";
import PrivacyCheckbox from "@/vue/common/PrivacyCheckbox.vue";
import * as Event from "@/event/Event";
import EventBus from "@/event/EventBus";
import HowItWorks from "@/vue/info/HowItWorks.vue";
import {mapState} from "vuex";

const PAYMENT_METHODS = [
	{key: 'visa', name: 'VISA'},
	{key: 'mastercard', name: 'MasterCard'},
	{key: 'amex', name: 'American Express'},
	{key: 'sepa', name: 'Banktransfer'},
	{key: 'klarna', name: 'Klarna'},
	{key: 'apple-pay', name: 'Apple Pay'},
];

export default {

	components: {
		HowItWorks,
		PrivacyCheckbox,
		PriceTable,
		CompleteData,
		InfoBox,
		Loading,
		RadioGroup,
		PopUp,
		FormGroup,
	},

	props: {
		isUpgrade: {
			type: Boolean,
			required: false,
			default: false,
		},
	},

	mounted ()
	{
		this.checkDataCompletion();

		if (this.isUpgrade)
		{
			this.loadUpgradableItems();
		}
		else
		{
			this.loadProducts();
		}
	},

	data ()
	{
		return {
			checkingDataCompletion: true,
			loadingProducts: true,
			product: undefined,
			products: undefined,
			upgradableItems: undefined,
			purchasing: false,
			error: false,
			dataComplete: undefined,
			hasMollieCustomerId: undefined,
			privacy: false,
		};
	},

	computed: {
		...mapState('auth', ['customer']),
		paymentMethods ()
		{
			return PAYMENT_METHODS;
		},
		loading ()
		{
			return this.checkingDataCompletion || this.loadingProducts;
		},
		productColor ()
		{
			return this.product
				? PRODUCT_COLORS[this.product.id]
				: undefined;
		},
		priceToPay ()
		{
			if (!this.product)
			{
				return undefined;
			}

			if (this.isUpgrade)
			{
				const upgradableItem = _.find(this.upgradableItems, item => item.product.id === this.product.id);

				if (!upgradableItem) // should not happen
				{
					return undefined;
				}

				return {price: upgradableItem.grossAmountToPayFormatted, taxRate: upgradableItem.taxRate};
			}

			return {price: this.product.grossAmountFormatted, taxRate: this.product.taxRate};
		},
		dataValid ()
		{
			return isDefined(this.product) && this.privacy === true;
		}
	},

	methods: {
		openFeedback ()
		{
			this.$emit('hidden');

			EventBus.$emit(Event.SHOW_FEEDBACK);
		},
		onChoseProduct ({product})
		{
			this.product = product;
		},
		async checkDataCompletion ()
		{
			this.checkingDataCompletion = true;

			const response = await new Get()
				.call();

			this.hasMollieCustomerId = response.data.customer.hasMollieCustomerId;
			this.dataComplete = response.data.customer.dataComplete;

			this.checkingDataCompletion = false;
		},
		async loadUpgradableItems ()
		{
			this.loadingProducts = true;

			const response = await new Info()
				.call();

			this.upgradableItems = response.data.upgradableItems;

			this.loadingProducts = false;
		},
		async loadProducts ()
		{
			this.loadingProducts = true;

			const response = await new GetList()
				.setCategory(SUBSCRIPTION)
				.call();

			this.products = response.data;

			this.loadingProducts = false;
		},
		async onOk ()
		{
			this.purchasing = true;

			try
			{
				this.error = false;

				const response = await new Purchase()
					.setProduct(this.product.id)
					.call();

				if (response.data.redirectUrl)
				{
					location.href = response.data.redirectUrl;
					return;
				}

				location.href = '/bezahlung/' + response.data.transaction.id;

				return;
			}
			catch (e)
			{
				this.error = true;
			}

			this.purchasing = false;
		}
	}
}
</script>
